import { BaseApiService } from './base.service';

/**
 * Combined invoice data interface
 */
export interface InvoiceCombinedData {
  invoice: {
    invoice_id: string;
    invoice_name: string;
    invoice_number: number;
    invoice_date: string;
    due_date: string;
    billing_period_start: string;
    billing_period_end: string;
    invoice_status: string;
    sending_status: string;
    payment_status: string;
    currency: string;
    subtotal: number;
    tax_rate: number;
    tax_amount: number;
    total_amount: number;
    notes?: string;
    terms_and_conditions?: string;
    service_description?: string;
  };
  invoice_lines: Array<{
    invoiceline_id: string;
    invoiceline_number: number;
    invoiceline_name: string;
    description: string;
    quantity: number;
    unit_price: number;
    line_total: number;
    tax_rate: number;
    tax_amount: number;
    line_billing_start: string;
    line_billing_end: string;
  }>;
  contract: {
    contract_id: string;
    contract_number: number;
    contract_name: string;
    start_date: string;
    end_date?: string;
    currency: string;
    language: string;
  };
  customer: {
    id: number;
    customer_type: string;
    customer_name: string;
    customer_contact_name?: string;
    customer_contact_role?: string;
    customer_address_street?: string;
    customer_address_postcode?: string;
    customer_address_city?: string;
    customer_address_country?: string;
    customer_address_state?: string;
    customer_telephone_number?: string;
    customer_email_address?: string;
    customer_tax_id?: string;
    customer_vat_number?: string;
    language: string;
  };
  company: {
    id: number;
    name: string;
    company_number?: string;
    contact_name?: string;
    contact_role?: string;
    telephone_number?: string;
    fax_number?: string;
    email_address?: string;
    support_email?: string;
    billing_email?: string;
    website_url?: string;
    address_street?: string;
    address_postcode?: string;
    address_city?: string;
    address_state?: string;
    address_country?: string;
    tax_id?: string;
    vat_number?: string;
    registration_number?: string;
    legal_form?: string;
    managing_director?: string;
    court_registration?: string;
    court_location?: string;
    bank_name?: string;
    bank_account?: string;
    bank_iban?: string;
    bank_bic?: string;
    bank_sort_code?: string;
    logo_url?: string;
    logo_path?: string;
    brand_color_primary?: string;
    brand_color_secondary?: string;
    footer_legal_text?: string;
    footer_payment_terms?: string;
  };
}

/**
 * Service for modern invoice operations with combined data
 */
export class ModernInvoiceService extends BaseApiService {
  /**
   * Get combined invoice data (company, contract, invoice, lines, customer)
   */
  async getCombinedInvoiceData(invoiceId: string): Promise<InvoiceCombinedData> {
    return this.get(`/api/v1/invoices-combined/${invoiceId}`);
  }

  /**
   * Generate modern PDF for an invoice
   */
  async generateModernPDF(invoiceId: string, language?: string): Promise<Blob> {
    // Build URL with optional language parameter
    let url = `/api/v1/invoices-combined/${invoiceId}/pdf`;
    if (language) {
      url += `?language=${language}`;
    }
    
    const response = await this.axiosInstance.get(url, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
    return response.data;
  }

  /**
   * Get PDF URL for modern invoice
   */
  getModernPDFUrl(invoiceId: string, language?: string): string {
    let url = `${1}/api/v1/v1/invoices-combined/${invoiceId}/pdf`;
    if (language) {
      url += `?language=${language}`;
    }
    return url;
  }

  /**
   * Download modern PDF and trigger browser download
   */
  async downloadModernPDF(invoiceId: string, language?: string, filename?: string): Promise<void> {
    try {
      const blob = await this.generateModernPDF(invoiceId, language);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `modern-invoice-${invoiceId}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading modern PDF:', error);
      throw error;
    }
  }

  /**
   * Open modern PDF in new window/tab
   */
  async openModernPDF(invoiceId: string, language?: string): Promise<void> {
    try {
      const blob = await this.generateModernPDF(invoiceId, language);
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window
      const newWindow = window.open(url, '_blank');
      
      if (!newWindow) {
        // Fallback: trigger download if popup blocked
        await this.downloadModernPDF(invoiceId, language);
      } else {
        // Cleanup URL after a delay to allow the window to load
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      }
    } catch (error) {
      console.error('Error opening modern PDF:', error);
      throw error;
    }
  }
}

export const modernInvoiceService = new ModernInvoiceService();